import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  Users, 
  Activity, 
  DollarSign, 
  TrendingUp, 
  Shield, 
  Database, 
  Server, 
  AlertTriangle, 
  Crown,
  Eye,
  Settings,
  BarChart3,
  MessageSquare,
  ShoppingBag,
  Zap,
  Globe,
  Lock,
  Bell,
  RefreshCw,
  Sparkles,
  ArrowUpRight,
  ArrowDownLeft,
  Clock,
  CheckCircle,
  XCircle,
  Wifi,
  Cpu,
  HardDrive,
  Monitor,
  Star,
  Calendar,
  MapPin,
  Coins,
  Target,
  PieChart,
  LineChart,
  Layers,
  Filter,
  Search,
  Download,
  Upload,
  AlertCircle,
  Info
} from 'lucide-react';
import { SuperAdminInit } from '@/components/admin/super-admin-init';
import { FeeManagementDashboard } from '@/components/admin/fee-management-dashboard';
import { SuperAdminService, type FeeInsights } from '@/lib/super-admin-service';
import { formatCurrency as formatCurrencyValue } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/components/ui/use-toast';
import {
  migrateNotificationActionUrls,
  purgeUserCompletely,
  verifyUserPurgeResidue,
  type MigrateNotificationActionUrlsResult,
  type PurgeUserCompletelyResult,
  type VerifyUserPurgeResidueResult,
} from '@/services/adminMaintenance';
import { db } from '@/lib/firebase';
import { addDoc, collection, limit, onSnapshot, orderBy, query, serverTimestamp } from 'firebase/firestore';
import {
  appendRuntimeResilienceHistoryPoint,
  clearRuntimeMetrics,
  getRuntimeDiagnosticsBundle,
  getRuntimeResilienceHistory,
  getRuntimeResilienceAnomalies,
  getRuntimeMetricsSnapshot,
  getRuntimeResilienceSummary,
  trackRuntimeMetric,
  type RuntimeResilienceHistoryPoint,
  type RuntimeMetricsSnapshot,
} from '@/lib/observability/runtime-metrics';
import { applyRecommendedRuntimeRecovery, clearRuntimeRecoveryCaches } from '@/lib/observability/runtime-recovery';

const AUTO_RECOVERY_ENABLED_KEY = 'intouch-runtime-auto-recovery-enabled';
const AUTO_RECOVERY_LAST_RUN_KEY = 'intouch-runtime-auto-recovery-last-run';
const AUTO_RECOVERY_COOLDOWN_MS = 1000 * 60 * 30;
const USERS_PER_PAGE = 25;

interface PlatformStats {
  totalUsers: number;
  activeUsers: number;
  totalPosts: number;
  totalMessages: number;
  totalTransactions: number;
  platformRevenue: number;
  totalGroups: number;
  totalEvents: number;
  totalMarketplaceItems: number;
  recentUsers: any[];
  recentPosts: any[];
  recentTransactions: any[];
  systemHealth: 'healthy' | 'warning' | 'critical';
  uptime: string;
  lastUpdated: string;
}

interface UserStats {
  totalUsers: number;
  adminUsers: number;
  sellerUsers: number;
  verifiedUsers: number;
  onlineUsers: number;
  roleDistribution: any;
  users: any[];
}

interface ContentStats {
  totalPosts: number;
  totalMessages: number;
  totalGroups: number;
  recentPosts: any[];
  recentMessages: any[];
  recentGroups: any[];
}

interface RunbookExecutionLog {
  id: string;
  actionId: string;
  title: string;
  scope: 'admin' | 'superadmin';
  status: 'success' | 'blocked' | 'error';
  details?: string;
  actorUid: string;
  actorEmail: string;
  actorRole: string;
  createdAt: number;
}

export function SuperAdminDashboard() {
  const { user, userData } = useAuth();
  const { toast } = useToast();

  const [notifMigrationLoading, setNotifMigrationLoading] = useState(false);
  const [notifMigrationPageSize, setNotifMigrationPageSize] = useState(200);
  const [notifMigrationIncludeMissing, setNotifMigrationIncludeMissing] = useState(false);
  const [notifMigrationNextToken, setNotifMigrationNextToken] = useState<string | null>(null);
  const [notifMigrationLastResult, setNotifMigrationLastResult] = useState<MigrateNotificationActionUrlsResult | null>(null);
  const [purgeTargetUserId, setPurgeTargetUserId] = useState('');
  const [purgeTargetEmail, setPurgeTargetEmail] = useState('');
  const [purgeConfirmText, setPurgeConfirmText] = useState('');
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [purgeResult, setPurgeResult] = useState<PurgeUserCompletelyResult | null>(null);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyResult, setVerifyResult] = useState<VerifyUserPurgeResidueResult | null>(null);
  const [stats, setStats] = useState<PlatformStats>({
    totalUsers: 0,
    activeUsers: 0,
    totalPosts: 0,
    totalMessages: 0,
    totalTransactions: 0,
    platformRevenue: 0,
    totalGroups: 0,
    totalEvents: 0,
    totalMarketplaceItems: 0,
    recentUsers: [],
    recentPosts: [],
    recentTransactions: [],
    systemHealth: 'healthy',
    uptime: '99.9%',
    lastUpdated: new Date().toISOString()
  });

  async function runNotificationMigration(dryRun: boolean) {
    try {
      setNotifMigrationLoading(true);
      const result = await migrateNotificationActionUrls({
        dryRun,
        pageSize: notifMigrationPageSize,
        startAfterId: notifMigrationNextToken ?? undefined,
        includeMissingActionUrl: notifMigrationIncludeMissing,
      });

      setNotifMigrationLastResult(result);
      setNotifMigrationNextToken(result.nextPageToken);

      toast({
        title: dryRun ? 'Dry run complete' : 'Migration complete',
        description: `${result.updated} updated out of ${result.processed} scanned${result.nextPageToken ? ' (more pages available)' : ''}.`,
      });
    } catch (err: any) {
      console.error('Notification migration failed:', err);
      toast({
        title: 'Migration failed',
        description: err?.message || 'Unexpected error',
        variant: 'destructive',
      });
    } finally {
      setNotifMigrationLoading(false);
    }
  }

  async function runHardDeleteUser(): Promise<void> {
    if (!isSuperAdmin) {
      toast({
        title: 'Permission required',
        description: 'Only Super Admin can permanently delete a user and all records.',
        variant: 'destructive',
      });
      return;
    }

    if (purgeConfirmText !== 'DELETE FOREVER') {
      toast({
        title: 'Confirmation mismatch',
        description: 'Type DELETE FOREVER exactly to proceed.',
        variant: 'destructive',
      });
      return;
    }

    if (!purgeTargetUserId.trim() && !purgeTargetEmail.trim()) {
      toast({
        title: 'Missing target',
        description: 'Provide a target User UID or email.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setPurgeLoading(true);
      const result = await purgeUserCompletely({
        userId: purgeTargetUserId.trim() || undefined,
        email: purgeTargetEmail.trim() || undefined,
        confirmText: purgeConfirmText,
      });

      setPurgeResult(result);
      toast({
        title: 'Hard delete completed',
        description: `Target ${result.targetUid} purged. Docs: ${result.deletedDocs}, Storage files: ${result.deletedStorageFiles}.`,
      });

      await loadRealPlatformData();
    } catch (error: any) {
      console.error('Hard delete failed:', error);
      toast({
        title: 'Hard delete failed',
        description: error?.message || 'Unexpected error while purging user.',
        variant: 'destructive',
      });
    } finally {
      setPurgeLoading(false);
    }
  }

  async function runPurgeVerification(): Promise<void> {
    if (!isSuperAdmin) {
      toast({
        title: 'Permission required',
        description: 'Only Super Admin can run post-purge verification.',
        variant: 'destructive',
      });
      return;
    }

    if (!purgeTargetUserId.trim() && !purgeTargetEmail.trim()) {
      toast({
        title: 'Missing target',
        description: 'Provide a target User UID or email to verify residue.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setVerifyLoading(true);
      const result = await verifyUserPurgeResidue({
        userId: purgeTargetUserId.trim() || undefined,
        email: purgeTargetEmail.trim() || undefined,
      });
      setVerifyResult(result);

      if (!result.authUserExists && result.totalFirestoreMatches === 0 && result.totalStorageFiles === 0) {
        toast({
          title: 'Verification clean',
          description: `No remaining references found for ${result.targetUid}.`,
        });
      } else {
        toast({
          title: 'Residue detected',
          description: `Found ${result.totalFirestoreMatches} Firestore matches and ${result.totalStorageFiles} storage files for ${result.targetUid}.`,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Post-purge verification failed:', error);
      toast({
        title: 'Verification failed',
        description: error?.message || 'Unexpected error while verifying residue.',
        variant: 'destructive',
      });
    } finally {
      setVerifyLoading(false);
    }
  }
  
  const [userStats, setUserStats] = useState<UserStats>({
    totalUsers: 0,
    adminUsers: 0,
    sellerUsers: 0,
    verifiedUsers: 0,
    onlineUsers: 0,
    roleDistribution: {},
    users: []
  });
  const [usersPage, setUsersPage] = useState(1);
  const [userSearchQuery, setUserSearchQuery] = useState('');

  const [contentStats, setContentStats] = useState<ContentStats>({
    totalPosts: 0,
    totalMessages: 0,
    totalGroups: 0,
    recentPosts: [],
    recentMessages: [],
    recentGroups: []
  });
  const [feeInsights, setFeeInsights] = useState<FeeInsights>({
    totalTransactions: 0,
    totalGrossRevenue: 0,
    totalFeesCollected: 0,
    currentMonthFeesCollected: 0,
    averageFeeRate: 0,
    serviceBreakdown: [],
  });

  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detailedAnalytics, setDetailedAnalytics] = useState<any>(null);
  const [systemMetrics, setSystemMetrics] = useState<any>(null);
  const [runtimeMetrics, setRuntimeMetrics] = useState<RuntimeMetricsSnapshot>(() => getRuntimeMetricsSnapshot());
  const [runtimeHistory, setRuntimeHistory] = useState<RuntimeResilienceHistoryPoint[]>(() => getRuntimeResilienceHistory());
  const [runbookHistory, setRunbookHistory] = useState<RunbookExecutionLog[]>([]);
  const [runbookHistoryLoading, setRunbookHistoryLoading] = useState(false);

  const filteredUsers = useMemo(() => {
    const normalizedQuery = userSearchQuery.trim().toLowerCase();
    if (!normalizedQuery) return userStats.users || [];

    return (userStats.users || []).filter((user: any) => {
      const name = String(user?.name || '').toLowerCase();
      const email = String(user?.email || '').toLowerCase();
      const role = String(user?.role || 'user').toLowerCase();
      const id = String(user?.id || '').toLowerCase();
      return (
        name.includes(normalizedQuery) ||
        email.includes(normalizedQuery) ||
        role.includes(normalizedQuery) ||
        id.includes(normalizedQuery)
      );
    });
  }, [userStats.users, userSearchQuery]);

  const totalUserPages = Math.max(1, Math.ceil((filteredUsers.length || 0) / USERS_PER_PAGE));
  const paginatedUsers = useMemo(() => {
    const start = (usersPage - 1) * USERS_PER_PAGE;
    return filteredUsers.slice(start, start + USERS_PER_PAGE);
  }, [filteredUsers, usersPage]);

  const visiblePageNumbers = useMemo(() => {
    if (totalUserPages <= 7) {
      return Array.from({ length: totalUserPages }, (_, idx) => idx + 1);
    }

    const pages: number[] = [1];
    const start = Math.max(2, usersPage - 1);
    const end = Math.min(totalUserPages - 1, usersPage + 1);

    if (start > 2) pages.push(-1);
    for (let page = start; page <= end; page += 1) pages.push(page);
    if (end < totalUserPages - 1) pages.push(-2);

    pages.push(totalUserPages);
    return pages;
  }, [usersPage, totalUserPages]);

  useEffect(() => {
    if (usersPage > totalUserPages) {
      setUsersPage(totalUserPages);
    }
  }, [usersPage, totalUserPages]);

  useEffect(() => {
    setUsersPage(1);
  }, [userSearchQuery]);
  const [opsQuery, setOpsQuery] = useState('');
  const [warRoomMode, setWarRoomMode] = useState(false);
  const [autoRecoveryEnabled, setAutoRecoveryEnabled] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(AUTO_RECOVERY_ENABLED_KEY) === '1';
  });
  const [lastAutoRecoveryAt, setLastAutoRecoveryAt] = useState<number>(() => {
    if (typeof window === 'undefined') return 0;
    const raw = window.localStorage.getItem(AUTO_RECOVERY_LAST_RUN_KEY);
    const value = raw ? Number(raw) : 0;
    return Number.isFinite(value) ? value : 0;
  });
  const isSuperAdmin = userData?.role === 'superadmin' && userData?.email === 'milan.krnjajic@gmail.com';
  const isAdmin = userData?.role === 'admin';
  const canAccessDashboard = isSuperAdmin || isAdmin;
  const opsPermissions = useMemo(() => ({
    canExportSnapshot: canAccessDashboard,
    canExportDiagnostics: canAccessDashboard,
    canRunRecovery: isSuperAdmin,
    canClearCaches: isSuperAdmin,
    canResetMetrics: isSuperAdmin,
    canRunSimulations: isSuperAdmin,
  }), [canAccessDashboard, isSuperAdmin]);
  const runtimeSummary = useMemo(() => getRuntimeResilienceSummary(), [runtimeMetrics, runtimeHistory]);
  const runtimeAnomalies = useMemo(() => getRuntimeResilienceAnomalies(), [runtimeHistory, runtimeMetrics]);
  const trendPoints = useMemo(() => [...runtimeHistory].slice(0, 12).reverse(), [runtimeHistory]);
  const runtimeIncidentFeed = useMemo(() => {
    const severityForKey = (key: string): 'info' | 'warning' | 'critical' => {
      if (key.includes('demo_mode') || key.includes('fetch.error') || key.includes('fallback_exhausted')) return 'critical';
      if (key.includes('fallback') || key.includes('permission_denied') || key.includes('timeout')) return 'warning';
      return 'info';
    };

    return runtimeMetrics.recentEvents.slice(0, 12).map((event) => ({
      ...event,
      severity: severityForKey(event.key),
    }));
  }, [runtimeMetrics]);

  const momentumScore = useMemo(() => {
    const activeRatio = stats.totalUsers > 0 ? (stats.activeUsers / stats.totalUsers) * 100 : 0;
    const recoveryWeight = Math.max(0, Math.min(100, runtimeSummary.resilienceScore));
    const commerceWeight = stats.totalTransactions > 0
      ? Math.min(100, (feeInsights.totalFeesCollected / Math.max(1, feeInsights.totalTransactions)) * 8)
      : 0;
    const score = Math.round((activeRatio * 0.45) + (recoveryWeight * 0.4) + (commerceWeight * 0.15));
    return Math.max(0, Math.min(100, score));
  }, [stats.totalUsers, stats.activeUsers, stats.totalTransactions, feeInsights.totalFeesCollected, feeInsights.totalTransactions, runtimeSummary.resilienceScore]);

  const riskRadar = useMemo(() => {
    const degradation = runtimeSummary.degradationEvents;
    const incidents = runtimeIncidentFeed.length;
    const permissionDenied = runtimeMetrics.counters['chat.typing.permission_denied'] || 0;
    const providerFailures = runtimeMetrics.counters['fx.provider.failure'] || 0;

    return [
      {
        domain: 'Infrastructure',
        risk: Math.min(100, degradation * 5 + providerFailures * 2),
        status: providerFailures > 10 ? 'elevated' : 'stable',
      },
      {
        domain: 'Messaging',
        risk: Math.min(100, permissionDenied * 6 + incidents * 1.2),
        status: permissionDenied > 8 ? 'watch' : 'stable',
      },
      {
        domain: 'Growth',
        risk: Math.max(5, 100 - momentumScore),
        status: momentumScore < 45 ? 'watch' : 'stable',
      },
      {
        domain: 'Commerce',
        risk: Math.min(100, Math.max(10, 80 - (feeInsights.averageFeeRate * 6))),
        status: feeInsights.averageFeeRate < 1.2 ? 'watch' : 'stable',
      },
    ];
  }, [runtimeSummary.degradationEvents, runtimeIncidentFeed.length, runtimeMetrics.counters, momentumScore, feeInsights.averageFeeRate]);

  const riskRadarTrendByDomain = useMemo(() => {
    const points = [...runtimeHistory].slice(0, 12).reverse();
    const statusBoost = (status: RuntimeResilienceHistoryPoint['status']) => {
      if (status === 'critical') return 24;
      if (status === 'degraded') return 12;
      return 0;
    };
    const clamp = (value: number) => Math.max(0, Math.min(100, Math.round(value)));

    const mapPoint = (point: RuntimeResilienceHistoryPoint, domain: string) => {
      const base = 100 - point.resilienceScore;
      const boost = statusBoost(point.status);

      if (domain === 'Infrastructure') return clamp(base * 0.9 + boost + (runtimeMetrics.counters['fx.provider.failure'] || 0) * 0.3);
      if (domain === 'Messaging') return clamp(base * 0.7 + boost * 0.75 + (runtimeMetrics.counters['chat.typing.permission_denied'] || 0) * 0.4);
      if (domain === 'Growth') return clamp(base * 0.65 + (100 - momentumScore) * 0.35);
      return clamp(base * 0.55 + Math.max(0, 30 - feeInsights.averageFeeRate * 8));
    };

    return {
      Infrastructure: points.map((point) => mapPoint(point, 'Infrastructure')),
      Messaging: points.map((point) => mapPoint(point, 'Messaging')),
      Growth: points.map((point) => mapPoint(point, 'Growth')),
      Commerce: points.map((point) => mapPoint(point, 'Commerce')),
    };
  }, [runtimeHistory, runtimeMetrics.counters, momentumScore, feeInsights.averageFeeRate]);

  const strategicInsights = useMemo(() => {
    const insights: string[] = [];
    const activeRatio = stats.totalUsers > 0 ? ((stats.activeUsers / stats.totalUsers) * 100).toFixed(1) : '0.0';

    insights.push(`Active user ratio is ${activeRatio}% with ${stats.activeUsers.toLocaleString()} users online in the latest window.`);
    insights.push(`Runtime resilience score is ${runtimeSummary.resilienceScore}/100 with ${runtimeSummary.degradationEvents} degradation signals in the latest cycle.`);
    insights.push(`Fee engine is averaging ${feeInsights.averageFeeRate.toFixed(2)}% across ${feeInsights.totalTransactions.toLocaleString()} monetized transactions.`);

    if (runtimeAnomalies.length > 0) {
      insights.push(`Detected ${runtimeAnomalies.length} runtime anomalies. Prioritize autopilot recovery and cache hygiene playbooks.`);
    }

    if (momentumScore < 45) {
      insights.push('Platform momentum is below target. Consider campaign boosts and re-engagement loops in social and content modules.');
    }

    return insights;
  }, [stats.totalUsers, stats.activeUsers, runtimeSummary.resilienceScore, runtimeSummary.degradationEvents, feeInsights.averageFeeRate, feeInsights.totalTransactions, runtimeAnomalies.length, momentumScore]);

  const saveRunbookExecution = async (entry: {
    actionId: string;
    title: string;
    scope: 'admin' | 'superadmin';
    status: 'success' | 'blocked' | 'error';
    details?: string;
  }) => {
    if (!user?.uid) return;

    try {
      await addDoc(collection(db, 'admin_ops_runbook_history'), {
        ...entry,
        actorUid: user.uid,
        actorEmail: user.email || userData?.email || '',
        actorRole: userData?.role || 'unknown',
        executedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Failed to persist runbook execution:', error);
    }
  };

  const runIncidentSimulation = async (scenario: 'fx' | 'chat' | 'exchange') => {
    if (!opsPermissions.canRunSimulations) {
      toast({
        title: 'Permission required',
        description: 'Incident simulations are restricted to Super Admin accounts.',
        variant: 'destructive',
      });
      await saveRunbookExecution({
        actionId: `simulate_${scenario}`,
        title: `Simulation blocked: ${scenario}`,
        scope: 'superadmin',
        status: 'blocked',
        details: 'Role does not permit simulation execution.',
      });
      return;
    }

    if (scenario === 'fx') {
      trackRuntimeMetric('fx.provider.failure', 1, { source: 'ops_lab_simulation', severity: 'high' });
      trackRuntimeMetric('fx.fallback.used', 1, { source: 'ops_lab_simulation', status: 'recovered' });
    }

    if (scenario === 'chat') {
      trackRuntimeMetric('chat.typing.permission_denied', 1, { source: 'ops_lab_simulation', severity: 'medium' });
      trackRuntimeMetric('chat.typing.listener_error', 1, { source: 'ops_lab_simulation', severity: 'medium' });
    }

    if (scenario === 'exchange') {
      trackRuntimeMetric('exchange.price_feed.demo_mode', 1, { source: 'ops_lab_simulation', severity: 'critical' });
      trackRuntimeMetric('exchange.swap.blocked_high_risk', 1, { source: 'ops_lab_simulation', severity: 'warning' });
    }

    appendRuntimeResilienceHistoryPoint();
    setRuntimeMetrics(getRuntimeMetricsSnapshot());
    setRuntimeHistory(getRuntimeResilienceHistory());

    await saveRunbookExecution({
      actionId: `simulate_${scenario}`,
      title: `Simulation executed: ${scenario}`,
      scope: 'superadmin',
      status: 'success',
      details: 'Synthetic incident drill injected into runtime telemetry.',
    });

    toast({
      title: 'Incident simulation executed',
      description: `Scenario "${scenario}" was injected into runtime telemetry for drill testing.`,
    });
  };

  const exportExecutiveSnapshot = async () => {
    if (!opsPermissions.canExportSnapshot) {
      toast({
        title: 'Permission required',
        description: 'You do not have access to export executive snapshots.',
        variant: 'destructive',
      });
      return;
    }

    const snapshot = {
      generatedAt: new Date().toISOString(),
      momentumScore,
      riskRadar,
      strategicInsights,
      runtimeSummary,
      runtimeAnomalies,
      stats,
      feeInsights,
      userStats,
    };

    const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `super-admin-executive-snapshot-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`;
    link.click();
    URL.revokeObjectURL(url);

    toast({
      title: 'Executive snapshot exported',
      description: 'Strategic summary package was downloaded successfully.',
    });

    await saveRunbookExecution({
      actionId: 'export_executive_snapshot',
      title: 'Executive snapshot exported',
      scope: 'admin',
      status: 'success',
      details: 'Operations package exported from Ops Lab.',
    });
  };

  const runOpsRunbookAction = async (actionId: 'recommended_recovery' | 'clear_caches' | 'reset_metrics' | 'export_diagnostics') => {
    if ((actionId === 'recommended_recovery' && !opsPermissions.canRunRecovery) ||
      (actionId === 'clear_caches' && !opsPermissions.canClearCaches) ||
      (actionId === 'reset_metrics' && !opsPermissions.canResetMetrics) ||
      (actionId === 'export_diagnostics' && !opsPermissions.canExportDiagnostics)) {
      toast({
        title: 'Permission required',
        description: 'This runbook action is not available for your role.',
        variant: 'destructive',
      });
      await saveRunbookExecution({
        actionId,
        title: `Runbook blocked: ${actionId}`,
        scope: actionId === 'export_diagnostics' ? 'admin' : 'superadmin',
        status: 'blocked',
        details: 'Role permission check failed.',
      });
      return;
    }

    try {
      if (actionId === 'recommended_recovery') {
        const result = applyRecommendedRuntimeRecovery();
        setRuntimeMetrics(getRuntimeMetricsSnapshot());
        setRuntimeHistory(getRuntimeResilienceHistory());
        toast({
          title: 'Recommended recovery executed',
          description: result.actions.join(' • '),
        });
        await saveRunbookExecution({
          actionId,
          title: 'Recommended recovery executed',
          scope: 'superadmin',
          status: 'success',
          details: result.actions.join(' • '),
        });
        return;
      }

      if (actionId === 'clear_caches') {
        const result = clearRuntimeRecoveryCaches();
        setRuntimeMetrics(getRuntimeMetricsSnapshot());
        setRuntimeHistory(getRuntimeResilienceHistory());
        toast({
          title: 'Recovery action completed',
          description: result.cleared.length > 0
            ? `Cleared caches: ${result.cleared.join(', ')}`
            : 'No caches were cleared in this environment.',
        });
        await saveRunbookExecution({
          actionId,
          title: 'Runtime caches clear executed',
          scope: 'superadmin',
          status: 'success',
          details: result.cleared.length > 0 ? result.cleared.join(', ') : 'No caches cleared',
        });
        return;
      }

      if (actionId === 'reset_metrics') {
        clearRuntimeMetrics();
        setRuntimeMetrics(getRuntimeMetricsSnapshot());
        setRuntimeHistory(getRuntimeResilienceHistory());
        toast({
          title: 'Runtime metrics reset',
          description: 'Reliability counters were cleared.',
        });
        await saveRunbookExecution({
          actionId,
          title: 'Runtime metrics reset',
          scope: 'superadmin',
          status: 'success',
          details: 'Reliability counters and trend snapshot reset.',
        });
        return;
      }

      const diagnostics = getRuntimeDiagnosticsBundle();
      const blob = new Blob([JSON.stringify(diagnostics, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `runtime-diagnostics-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`;
      link.click();
      URL.revokeObjectURL(url);
      toast({
        title: 'Diagnostics exported',
        description: 'Runtime diagnostics bundle has been downloaded.',
      });
      await saveRunbookExecution({
        actionId,
        title: 'Diagnostics exported',
        scope: 'admin',
        status: 'success',
        details: 'Runtime diagnostics bundle downloaded.',
      });
    } catch (error: any) {
      toast({
        title: 'Runbook action failed',
        description: error?.message || 'Unexpected error while executing action.',
        variant: 'destructive',
      });
      await saveRunbookExecution({
        actionId,
        title: `Runbook error: ${actionId}`,
        scope: actionId === 'export_diagnostics' ? 'admin' : 'superadmin',
        status: 'error',
        details: error?.message || 'Unexpected error',
      });
    }
  };

  useEffect(() => {
    if (canAccessDashboard) {
      loadRealPlatformData();
      appendRuntimeResilienceHistoryPoint();
      setRuntimeMetrics(getRuntimeMetricsSnapshot());
      setRuntimeHistory(getRuntimeResilienceHistory());
      // Auto-refresh every 5 minutes
      const interval = setInterval(loadRealPlatformData, 300000);
      return () => clearInterval(interval);
    }
  }, [canAccessDashboard]);

  useEffect(() => {
    if (!canAccessDashboard) return;

    const interval = setInterval(() => {
      appendRuntimeResilienceHistoryPoint();
      setRuntimeMetrics(getRuntimeMetricsSnapshot());
      setRuntimeHistory(getRuntimeResilienceHistory());
    }, 15000);

    return () => clearInterval(interval);
  }, [canAccessDashboard]);

  useEffect(() => {
    if (!canAccessDashboard) {
      setRunbookHistory([]);
      return;
    }

    setRunbookHistoryLoading(true);
    const q = query(collection(db, 'admin_ops_runbook_history'), orderBy('executedAt', 'desc'), limit(25));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const nextLogs: RunbookExecutionLog[] = snapshot.docs.map((item) => {
        const data = item.data() as any;
        const rawTimestamp = data.executedAt;
        const createdAt = rawTimestamp && typeof rawTimestamp.toMillis === 'function'
          ? rawTimestamp.toMillis()
          : 0;

        return {
          id: item.id,
          actionId: data.actionId || 'unknown',
          title: data.title || 'Untitled runbook action',
          scope: data.scope === 'superadmin' ? 'superadmin' : 'admin',
          status: data.status === 'blocked' || data.status === 'error' ? data.status : 'success',
          details: data.details || '',
          actorUid: data.actorUid || 'unknown',
          actorEmail: data.actorEmail || 'unknown',
          actorRole: data.actorRole || 'unknown',
          createdAt,
        };
      });

      setRunbookHistory(nextLogs);
      setRunbookHistoryLoading(false);
    }, (error) => {
      console.error('Failed to subscribe to runbook history:', error);
      setRunbookHistoryLoading(false);
    });

    return () => unsubscribe();
  }, [canAccessDashboard]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(AUTO_RECOVERY_ENABLED_KEY, autoRecoveryEnabled ? '1' : '0');
  }, [autoRecoveryEnabled]);

  useEffect(() => {
    if (typeof window === 'undefined' || !Number.isFinite(lastAutoRecoveryAt)) return;
    window.localStorage.setItem(AUTO_RECOVERY_LAST_RUN_KEY, String(lastAutoRecoveryAt));
  }, [lastAutoRecoveryAt]);

  useEffect(() => {
    if (!isSuperAdmin || !autoRecoveryEnabled) return;

    const hasSevereAnomaly = runtimeAnomalies.some((anomaly) => anomaly.severity === 'critical' || anomaly.severity === 'high');
    if (!hasSevereAnomaly) return;

    const now = Date.now();
    if (lastAutoRecoveryAt > 0 && now - lastAutoRecoveryAt < AUTO_RECOVERY_COOLDOWN_MS) return;

    const result = applyRecommendedRuntimeRecovery();
    if (result.actions.some((action) => action !== 'No automated recovery action required')) {
      setLastAutoRecoveryAt(now);
      trackRuntimeMetric('runtime.recovery.autopilot_triggered', 1, { status: result.summaryStatus });
      setRuntimeMetrics(getRuntimeMetricsSnapshot());
      setRuntimeHistory(getRuntimeResilienceHistory());
      toast({
        title: 'Auto-recovery executed',
        description: result.actions.join(' • '),
      });
    }
  }, [autoRecoveryEnabled, isSuperAdmin, lastAutoRecoveryAt, runtimeAnomalies, toast]);

  const loadRealPlatformData = async () => {
    try {
      setLoading(true);
      
      // Load all statistics in parallel
      const [platformStats, userStatistics, contentStatistics, activity, analytics, metrics, fees] = await Promise.all([
        SuperAdminService.getRealPlatformStats(),
        SuperAdminService.getUserStats(),
        SuperAdminService.getContentStats(),
        SuperAdminService.getRecentActivity(),
        SuperAdminService.getDetailedAnalytics(),
        SuperAdminService.getSystemMetrics(),
        SuperAdminService.getFeeInsights(),
      ]);

      setStats(platformStats);
      setUserStats(userStatistics);
      setContentStats(contentStatistics);
      setRecentActivity(activity);
      setDetailedAnalytics(analytics);
      setSystemMetrics(metrics);
      setFeeInsights(fees);
      appendRuntimeResilienceHistoryPoint();
      setRuntimeMetrics(getRuntimeMetricsSnapshot());
      setRuntimeHistory(getRuntimeResilienceHistory());

      console.log('✅ Real platform data loaded successfully:', {
        totalUsers: platformStats.totalUsers,
        totalPosts: platformStats.totalPosts,
        totalMessages: platformStats.totalMessages,
        totalGroups: platformStats.totalGroups,
        totalVideos: platformStats.totalVideos,
        totalMusic: platformStats.totalTracks,
        totalPhotos: platformStats.totalPhotos,
        totalHotels: platformStats.totalHotels,
        totalRecipes: platformStats.totalRecipes
      });

    } catch (error) {
      console.error('❌ Error loading real platform data:', error);
      toast({
        title: 'Error Loading Data',
        description: 'Failed to load platform statistics. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadRealPlatformData();
    setIsRefreshing(false);
    toast({
      title: 'Data Refreshed',
      description: 'Real platform data has been updated successfully.',
    });
  };

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'healthy': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'critical': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'N/A';
    }
  };

  const formatCurrency = (amount: number) => {
    return formatCurrencyValue(amount || 0);
  };

  if (!canAccessDashboard) {
    return (
      <div className="max-w-7xl mx-auto py-8 px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-4">Admin Command Dashboard</h1>
          <p className="text-muted-foreground">Initialize elevated admin access to view this dashboard</p>
        </div>
        <SuperAdminInit />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-8 px-4">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="text-muted-foreground">Loading real platform data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-shell min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-700 via-blue-600 to-cyan-600 motion-enter reveal-1">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-cyan-300/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-300/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex justify-between items-center mb-12">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-2xl">
                <Crown className="w-8 h-8 text-white" />
              </div>
              <div>
                <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full mb-2 text-sm">
                  <Sparkles className="w-3 h-3" />
                  <span>Super Admin Access</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-2 leading-tight">
                  Platform Command Center
                </h1>
                <p className="text-base md:text-xl text-white/90 leading-relaxed">Welcome back, Milan! Real-time InTouch analytics</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                <div className="flex items-center gap-2 text-white text-sm">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span>Live Data</span>
                </div>
                <div className="text-xs text-white/70 mt-1">
                  Last update: {new Date().toLocaleTimeString()}
                </div>
              </div>
              <Button 
                onClick={handleRefresh} 
                disabled={isRefreshing} 
                className="bg-white/90 hover:bg-white text-gray-900 font-semibold"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          {/* Advanced Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div className="text-3xl font-bold text-white mb-1">{stats.totalUsers.toLocaleString()}</div>
              <div className="text-white/80 text-sm mb-2">Total Users</div>
              <div className="flex items-center justify-center gap-1 text-xs text-green-300">
                <ArrowUpRight className="w-3 h-3" />
                <span>{stats.activeUsers} active today</span>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
              <div className="w-12 h-12 bg-gradient-to-r from-green-400 to-emerald-400 rounded-xl flex items-center justify-center mx-auto mb-3">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
              <div className="text-3xl font-bold text-white mb-1">{stats.totalPosts.toLocaleString()}</div>
              <div className="text-white/80 text-sm mb-2">Content Posts</div>
              <div className="flex items-center justify-center gap-1 text-xs text-green-300">
                <ArrowUpRight className="w-3 h-3" />
                <span>{stats.totalMessages.toLocaleString()} messages</span>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-xl flex items-center justify-center mx-auto mb-3">
                <ShoppingBag className="w-6 h-6 text-white" />
              </div>
              <div className="text-3xl font-bold text-white mb-1">{stats.totalMarketplaceItems.toLocaleString()}</div>
              <div className="text-white/80 text-sm mb-2">Marketplace</div>
              <div className="flex items-center justify-center gap-1 text-xs text-green-300">
                <ArrowUpRight className="w-3 h-3" />
                <span>{stats.totalTransactions} transactions</span>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
              <div className="w-12 h-12 bg-gradient-to-r from-orange-400 to-red-400 rounded-xl flex items-center justify-center mx-auto mb-3">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div className="text-3xl font-bold text-white mb-1">{stats.systemHealth === 'healthy' ? '99.9' : '98.5'}%</div>
              <div className="text-white/80 text-sm mb-2">System Health</div>
              <div className="flex items-center justify-center gap-1 text-xs text-green-300">
                <CheckCircle className="w-3 h-3" />
                <span>All systems online</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 motion-enter reveal-2">
        {/* Real-time Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <Badge className="bg-green-100 text-green-700 border-green-200">
                  <ArrowUpRight className="w-3 h-3 mr-1" />
                  +12%
                </Badge>
              </div>
              <div className="space-y-2">
                <div className="text-2xl font-bold text-gray-900">{stats.totalUsers.toLocaleString()}</div>
                <div className="text-sm text-gray-600">Total Users</div>
                <Progress value={75} className="h-2" />
                <div className="text-xs text-gray-500">{stats.activeUsers} active in 24h</div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
                <Badge className="bg-green-100 text-green-700 border-green-200">
                  <ArrowUpRight className="w-3 h-3 mr-1" />
                  +8%
                </Badge>
              </div>
              <div className="space-y-2">
                <div className="text-2xl font-bold text-gray-900">{stats.totalPosts.toLocaleString()}</div>
                <div className="text-sm text-gray-600">Content Posts</div>
                <Progress value={85} className="h-2" />
                <div className="text-xs text-gray-500">{stats.totalMessages.toLocaleString()} messages</div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  Stable
                </Badge>
              </div>
              <div className="space-y-2">
                <div className="text-2xl font-bold text-gray-900">{stats.totalGroups.toLocaleString()}</div>
                <div className="text-sm text-gray-600">Communities</div>
                <Progress value={60} className="h-2" />
                <div className="text-xs text-gray-500">{stats.totalEvents} events</div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                  <ShoppingBag className="w-6 h-6 text-white" />
                </div>
                <Badge className="bg-green-100 text-green-700 border-green-200">
                  <ArrowUpRight className="w-3 h-3 mr-1" />
                  +15%
                </Badge>
              </div>
              <div className="space-y-2">
                <div className="text-2xl font-bold text-gray-900">{stats.totalMarketplaceItems.toLocaleString()}</div>
                <div className="text-sm text-gray-600">Marketplace Items</div>
                <Progress value={92} className="h-2" />
                <div className="text-xs text-gray-500">{stats.totalTransactions} transactions</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* System Health Overview */}
        <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm mb-12">
          <CardContent className="p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">System Health Monitor</h3>
                <p className="text-gray-600">Real-time platform performance and status</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-green-700">All Systems Operational</span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Database className="w-8 h-8 text-white" />
                </div>
                <div className="text-2xl font-bold text-green-600 mb-1">99.9%</div>
                <div className="text-sm text-gray-600">Database Uptime</div>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Server className="w-8 h-8 text-white" />
                </div>
                <div className="text-2xl font-bold text-blue-600 mb-1">45ms</div>
                <div className="text-sm text-gray-600">Response Time</div>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Cpu className="w-8 h-8 text-white" />
                </div>
                <div className="text-2xl font-bold text-cyan-600 mb-1">23%</div>
                <div className="text-sm text-gray-600">CPU Usage</div>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <HardDrive className="w-8 h-8 text-white" />
                </div>
                <div className="text-2xl font-bold text-orange-600 mb-1">78%</div>
                <div className="text-sm text-gray-600">Storage Used</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Dashboard Tabs */}
        <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-9">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="fees">Fees Session</TabsTrigger>
          <TabsTrigger value="ops">Ops Lab</TabsTrigger>
          <TabsTrigger value="system">System</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Users */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Recent Users
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats.recentUsers.slice(0, 5).map((user, index) => (
                    <div key={user.id || index} className="flex items-center gap-3 p-3 bg-accent/50 rounded-lg">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <Users className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">{user.name || user.email}</div>
                        <div className="text-sm text-muted-foreground">
                          Joined {formatDate(user.createdAt)}
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {user.role || 'user'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recentActivity.slice(0, 5).map((activity, index) => (
                    <div key={activity.id || index} className="flex items-start gap-3 p-3 bg-accent/50 rounded-lg">
                      <div className="w-8 h-8 bg-success/10 rounded-full flex items-center justify-center">
                        <Activity className="w-4 h-4 text-success" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{activity.message}</div>
                        <div className="text-xs text-muted-foreground">
                          {formatDate(activity.timestamp)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Content Breakdown */}
            <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="w-5 h-5" />
                  Content Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                {detailedAnalytics && detailedAnalytics.contentBreakdown && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Videos</span>
                      <Badge className="bg-cyan-100 text-cyan-700">{detailedAnalytics.contentBreakdown.videos}</Badge>
                    </div>
                    <Progress value={(detailedAnalytics.contentBreakdown.videos / detailedAnalytics.contentBreakdown.total) * 100} className="h-2" />
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Images</span>
                      <Badge className="bg-blue-100 text-blue-700">{detailedAnalytics.contentBreakdown.images}</Badge>
                    </div>
                    <Progress value={(detailedAnalytics.contentBreakdown.images / detailedAnalytics.contentBreakdown.total) * 100} className="h-2" />
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Text Only</span>
                      <Badge className="bg-green-100 text-green-700">{detailedAnalytics.contentBreakdown.text}</Badge>
                    </div>
                    <Progress value={(detailedAnalytics.contentBreakdown.text / detailedAnalytics.contentBreakdown.total) * 100} className="h-2" />
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Mixed Media</span>
                      <Badge className="bg-orange-100 text-orange-700">{detailedAnalytics.contentBreakdown.mixed}</Badge>
                    </div>
                    <Progress value={(detailedAnalytics.contentBreakdown.mixed / detailedAnalytics.contentBreakdown.total) * 100} className="h-2" />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Booking Statistics */}
            <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Booking & Travel
                </CardTitle>
              </CardHeader>
              <CardContent>
                {detailedAnalytics && detailedAnalytics.bookings && (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <span className="text-sm font-medium">Hotels</span>
                      <span className="text-lg font-bold text-blue-600">{detailedAnalytics.bookings.totalHotels}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-cyan-50 rounded-lg">
                      <span className="text-sm font-medium">Apartments</span>
                      <span className="text-lg font-bold text-cyan-600">{detailedAnalytics.bookings.totalApartments}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-lg">
                      <span className="text-sm font-medium">Total Listings</span>
                      <span className="text-lg font-bold text-gray-900">{detailedAnalytics.bookings.totalBookingListings}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Food & Dining */}
            <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5" />
                  Food & Dining
                </CardTitle>
              </CardHeader>
              <CardContent>
                {detailedAnalytics && detailedAnalytics.food && (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                      <span className="text-sm font-medium">Food Listings</span>
                      <span className="text-lg font-bold text-orange-600">{detailedAnalytics.food.totalFoodListings}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                      <span className="text-sm font-medium">Restaurants</span>
                      <span className="text-lg font-bold text-red-600">{detailedAnalytics.food.totalRestaurants}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                      <span className="text-sm font-medium">Recipes</span>
                      <span className="text-lg font-bold text-green-600">{detailedAnalytics.food.totalRecipes}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gradient-to-r from-orange-100 to-green-100 rounded-lg">
                      <span className="text-sm font-medium">Total Food Content</span>
                      <span className="text-lg font-bold text-gray-900">{detailedAnalytics.food.totalFoodContent}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Entertainment & Social */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5" />
                  Entertainment
                </CardTitle>
              </CardHeader>
              <CardContent>
                {detailedAnalytics && detailedAnalytics.entertainment && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Star className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-2xl font-bold text-cyan-700">{detailedAnalytics.entertainment.totalMusic}</div>
                      <div className="text-xs text-cyan-600">Music Tracks</div>
                    </div>
                    <div className="text-center p-4 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Eye className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-2xl font-bold text-blue-700">{detailedAnalytics.entertainment.totalPhotos}</div>
                      <div className="text-xs text-blue-600">Photos</div>
                    </div>
                    <div className="text-center p-4 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl">
                      <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Layers className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-2xl font-bold text-green-700">{detailedAnalytics.entertainment.totalAlbums}</div>
                      <div className="text-xs text-green-600">Albums</div>
                    </div>
                    <div className="text-center p-4 bg-gradient-to-br from-red-100 to-orange-100 rounded-xl">
                      <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Monitor className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-2xl font-bold text-red-700">{detailedAnalytics.entertainment.totalVideos}</div>
                      <div className="text-xs text-red-600">Videos</div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Social Engagement
                </CardTitle>
              </CardHeader>
              <CardContent>
                {detailedAnalytics && detailedAnalytics.social && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl">
                      <div>
                        <div className="text-2xl font-bold text-blue-700">{detailedAnalytics.social.totalFriendships}</div>
                        <div className="text-sm text-blue-600">Total Friendships</div>
                      </div>
                      <Users className="w-10 h-10 text-blue-500" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
                      <div>
                        <div className="text-2xl font-bold text-green-700">{detailedAnalytics.social.totalCalls}</div>
                        <div className="text-sm text-green-600">Total Calls</div>
                      </div>
                      <Wifi className="w-10 h-10 text-green-500" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl">
                      <div>
                        <div className="text-2xl font-bold text-cyan-700">{stats.totalGroups}</div>
                        <div className="text-sm text-cyan-600">Active Groups</div>
                      </div>
                      <Users className="w-10 h-10 text-cyan-500" />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Marketplace & Transportation */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5" />
                  Marketplace
                </CardTitle>
              </CardHeader>
              <CardContent>
                {detailedAnalytics && detailedAnalytics.marketplace && (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <span className="text-sm font-medium">Products</span>
                      <span className="text-lg font-bold text-blue-600">{detailedAnalytics.marketplace.totalProducts}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                      <span className="text-sm font-medium">Orders</span>
                      <span className="text-lg font-bold text-green-600">{detailedAnalytics.marketplace.totalOrders}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-cyan-50 rounded-lg">
                      <span className="text-sm font-medium">Conversion Rate</span>
                      <span className="text-lg font-bold text-cyan-600">{detailedAnalytics.marketplace.conversionRate}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Transportation
                </CardTitle>
              </CardHeader>
              <CardContent>
                {detailedAnalytics && detailedAnalytics.transportation && (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                      <span className="text-sm font-medium">Tickets</span>
                      <span className="text-lg font-bold text-orange-600">{detailedAnalytics.transportation.totalTickets}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <span className="text-sm font-medium">Transport Tickets</span>
                      <span className="text-lg font-bold text-blue-600">{detailedAnalytics.transportation.totalTransportTickets}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                      <span className="text-sm font-medium">Bookings</span>
                      <span className="text-lg font-bold text-green-600">{detailedAnalytics.transportation.totalTransportBookings}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Events & Campaigns
                </CardTitle>
              </CardHeader>
              <CardContent>
                {detailedAnalytics && detailedAnalytics.eventsAndCampaigns && (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-cyan-50 rounded-lg">
                      <span className="text-sm font-medium">Events</span>
                      <span className="text-lg font-bold text-cyan-600">{detailedAnalytics.eventsAndCampaigns.totalEvents}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-sky-50 rounded-lg">
                      <span className="text-sm font-medium">Campaigns</span>
                      <span className="text-lg font-bold text-sky-600">{detailedAnalytics.eventsAndCampaigns.totalCampaigns}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-lg">
                      <span className="text-sm font-medium">Total Promotions</span>
                      <span className="text-lg font-bold text-gray-900">{detailedAnalytics.eventsAndCampaigns.totalPromotions}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Learning & Pages */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Learning & Resources
                </CardTitle>
              </CardHeader>
              <CardContent>
                {detailedAnalytics && detailedAnalytics.learning && (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-xl">
                      <div className="text-2xl font-bold text-blue-700">{detailedAnalytics.learning.totalTutorials}</div>
                      <div className="text-xs text-blue-600 mt-1">Tutorials</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-xl">
                      <div className="text-2xl font-bold text-green-700">{detailedAnalytics.learning.totalClassifieds}</div>
                      <div className="text-xs text-green-600 mt-1">Classifieds</div>
                    </div>
                    <div className="text-center p-4 bg-cyan-50 rounded-xl">
                      <div className="text-2xl font-bold text-cyan-700">{detailedAnalytics.learning.totalCoupons}</div>
                      <div className="text-xs text-cyan-600 mt-1">Coupons</div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Pages & Communities
                </CardTitle>
              </CardHeader>
              <CardContent>
                {detailedAnalytics && detailedAnalytics.pages && (
                  <div className="flex items-center justify-center p-8">
                    <div className="text-center">
                      <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Globe className="w-10 h-10 text-white" />
                      </div>
                      <div className="text-4xl font-bold text-gray-900 mb-2">{detailedAnalytics.pages.totalPages}</div>
                      <div className="text-sm text-gray-600">Total Business Pages</div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* System Metrics */}
          {systemMetrics && (
            <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  System Metrics & Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="text-center p-4 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl">
                    <Database className="w-10 h-10 text-blue-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-blue-700">{systemMetrics.totalDocuments}</div>
                    <div className="text-xs text-blue-600">Total Documents</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl">
                    <Layers className="w-10 h-10 text-green-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-green-700">{systemMetrics.totalCollections}</div>
                    <div className="text-xs text-green-600">Active Collections</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl">
                    <Zap className="w-10 h-10 text-cyan-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-cyan-700">{systemMetrics.performance.responseTime}</div>
                    <div className="text-xs text-cyan-600">Response Time</div>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-orange-100 to-red-100 rounded-xl">
                    <HardDrive className="w-10 h-10 text-orange-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-orange-700">{systemMetrics.storage.estimatedSize}</div>
                    <div className="text-xs text-orange-600">Estimated Storage</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="users" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>User Management</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{userStats.totalUsers.toLocaleString()}</div>
                    <p className="text-xs text-muted-foreground">Total Users</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{userStats.adminUsers.toLocaleString()}</div>
                    <p className="text-xs text-muted-foreground">Admin Users</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{userStats.sellerUsers.toLocaleString()}</div>
                    <p className="text-xs text-muted-foreground">Seller Users</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{userStats.onlineUsers.toLocaleString()}</div>
                    <p className="text-xs text-muted-foreground">Online Now</p>
                  </CardContent>
                </Card>
              </div>

              {/* Users List */}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-semibold">All Users</h3>
                  <div className="text-xs text-muted-foreground">
                    Showing {(filteredUsers.length || 0) === 0 ? 0 : (usersPage - 1) * USERS_PER_PAGE + 1}
                    {' '}-{' '}
                    {Math.min(usersPage * USERS_PER_PAGE, filteredUsers.length || 0)} of {filteredUsers.length || 0}
                    {userSearchQuery.trim() ? ` (filtered from ${userStats.users?.length || 0})` : ''}
                  </div>
                </div>

                <div className="relative max-w-md">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    value={userSearchQuery}
                    onChange={(event) => setUserSearchQuery(event.target.value)}
                    placeholder="Search by name, email, role, or UID"
                    className="h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm"
                  />
                </div>

                {paginatedUsers.map((user, index) => (
                  <div key={user.id || index} className="flex items-center justify-between p-3 bg-accent/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <Users className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium">{user.name || user.email}</div>
                        <div className="text-sm text-muted-foreground">{user.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{user.role || 'user'}</Badge>
                      {user.isOnline && <div className="w-2 h-2 bg-success rounded-full"></div>}
                      {isSuperAdmin && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            setPurgeTargetUserId(user.id || '');
                            setPurgeTargetEmail(user.email || '');
                          }}
                        >
                          Purge Target
                        </Button>
                      )}
                    </div>
                  </div>
                ))}

                {paginatedUsers.length === 0 && (
                  <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground text-center">
                    No users found.
                  </div>
                )}

                {filteredUsers.length > USERS_PER_PAGE && (
                  <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                    <div className="text-xs text-muted-foreground">
                      Page {usersPage} of {totalUserPages}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={usersPage <= 1}
                        onClick={() => setUsersPage((prev) => Math.max(1, prev - 1))}
                      >
                        Previous
                      </Button>
                      {visiblePageNumbers.map((pageNumber, index) =>
                        pageNumber < 0 ? (
                          <span key={`ellipsis-${index}`} className="px-1 text-muted-foreground">...</span>
                        ) : (
                          <Button
                            key={pageNumber}
                            variant={pageNumber === usersPage ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setUsersPage(pageNumber)}
                          >
                            {pageNumber}
                          </Button>
                        )
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={usersPage >= totalUserPages}
                        onClick={() => setUsersPage((prev) => Math.min(totalUserPages, prev + 1))}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-8 rounded-xl border border-red-200 bg-red-50 p-4 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-red-800">Permanent User Purge (Irreversible)</h3>
                    <p className="text-sm text-red-700 mt-1">
                      Deletes the user from Authentication and purges matched Firestore and Storage records. This action cannot be undone.
                    </p>
                  </div>
                  <Badge className="bg-red-100 text-red-800 border-red-300">Super Admin Only</Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-red-800 font-medium">Target User UID</label>
                    <input
                      value={purgeTargetUserId}
                      onChange={(event) => setPurgeTargetUserId(event.target.value)}
                      placeholder="e.g. abc123UID"
                      className="mt-1 w-full h-10 rounded-md border border-red-300 bg-white px-3 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-red-800 font-medium">Target Email (optional if UID provided)</label>
                    <input
                      value={purgeTargetEmail}
                      onChange={(event) => setPurgeTargetEmail(event.target.value)}
                      placeholder="user@example.com"
                      className="mt-1 w-full h-10 rounded-md border border-red-300 bg-white px-3 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-red-800 font-medium">Type DELETE FOREVER to confirm</label>
                  <input
                    value={purgeConfirmText}
                    onChange={(event) => setPurgeConfirmText(event.target.value)}
                    placeholder="DELETE FOREVER"
                    className="mt-1 w-full md:w-[280px] h-10 rounded-md border border-red-300 bg-white px-3 text-sm"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="destructive"
                    disabled={!isSuperAdmin || purgeLoading || purgeConfirmText !== 'DELETE FOREVER'}
                    onClick={() => {
                      void runHardDeleteUser();
                    }}
                  >
                    {purgeLoading ? 'Purging user...' : 'Delete User and All Records'}
                  </Button>
                  <Button
                    variant="outline"
                    disabled={!isSuperAdmin || verifyLoading || purgeLoading}
                    onClick={() => {
                      void runPurgeVerification();
                    }}
                  >
                    {verifyLoading ? 'Verifying...' : 'Run Post-Purge Verification'}
                  </Button>
                  {!isSuperAdmin && (
                    <span className="text-xs text-red-700">Only Super Admin can execute this action.</span>
                  )}
                </div>

                {purgeResult && (
                  <div className="rounded-md border border-red-200 bg-white p-3 text-xs overflow-auto max-h-56">
                    <pre>{JSON.stringify(purgeResult, null, 2)}</pre>
                  </div>
                )}

                {verifyResult && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs overflow-auto max-h-64">
                    <div className="font-semibold text-amber-800 mb-2">Post-Purge Verification Report</div>
                    <pre>{JSON.stringify(verifyResult, null, 2)}</pre>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="content" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Content Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{contentStats.totalPosts.toLocaleString()}</div>
                    <p className="text-xs text-muted-foreground">Total Posts</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{contentStats.totalMessages.toLocaleString()}</div>
                    <p className="text-xs text-muted-foreground">Total Messages</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{contentStats.totalGroups.toLocaleString()}</div>
                    <p className="text-xs text-muted-foreground">Total Groups</p>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Posts */}
              <div className="space-y-2">
                <h3 className="font-semibold">Recent Posts</h3>
                {contentStats.recentPosts.slice(0, 5).map((post, index) => (
                  <div key={post.id || index} className="p-3 bg-accent/50 rounded-lg">
                    <div className="font-medium text-sm">{post.content?.substring(0, 100)}...</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {formatDate(post.createdAt)} • {post.authorName || 'Unknown'}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activity" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Platform Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentActivity.map((activity, index) => (
                  <div key={activity.id || index} className="flex items-start gap-3 p-3 bg-accent/50 rounded-lg">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Activity className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{activity.message}</div>
                      <div className="text-xs text-muted-foreground">
                        {formatDate(activity.timestamp)} • {activity.type}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="fees" className="space-y-6">
          <Card className="border-0 shadow-xl bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Fees Session
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Coins className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-green-700 mb-1">{formatCurrency(feeInsights.totalFeesCollected)}</div>
                  <div className="text-sm text-green-600">Fees Collected (All-Time)</div>
                </div>
                
                <div className="text-center p-6 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-blue-700 mb-1">{feeInsights.averageFeeRate.toFixed(2)}%</div>
                  <div className="text-sm text-blue-600">Average Effective Fee Rate</div>
                </div>
                
                <div className="text-center p-6 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-cyan-700 mb-1">{formatCurrency(feeInsights.currentMonthFeesCollected)}</div>
                  <div className="text-sm text-cyan-600">Current Month Fees</div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                  <div className="text-sm text-slate-600">Fee-bearing Transactions</div>
                  <div className="text-2xl font-bold text-slate-900">{feeInsights.totalTransactions.toLocaleString()}</div>
                </div>
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                  <div className="text-sm text-slate-600">Gross Revenue Observed</div>
                  <div className="text-2xl font-bold text-slate-900">{formatCurrency(feeInsights.totalGrossRevenue)}</div>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <div className="text-sm font-semibold text-slate-700">Top Services by Collected Fees</div>
                {feeInsights.serviceBreakdown.length === 0 ? (
                  <div className="text-sm text-slate-500 p-3 rounded-lg bg-slate-50 border border-slate-200">
                    No transaction fee breakdown data found yet.
                  </div>
                ) : (
                  feeInsights.serviceBreakdown.map((row) => (
                    <div key={row.service} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                      <div>
                        <div className="font-medium text-slate-900">{row.service}</div>
                        <div className="text-xs text-slate-500">{row.transactions} tx • {row.averageFeeRate.toFixed(2)}% avg fee</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-slate-900">{formatCurrency(row.feesCollected)}</div>
                        <div className="text-xs text-slate-500">of {formatCurrency(row.grossRevenue)}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          <FeeManagementDashboard />
        </TabsContent>

        <TabsContent value="ops" className="space-y-6">
          <Card className={`border-0 shadow-xl backdrop-blur-sm ${warRoomMode ? 'bg-slate-900 text-slate-100' : 'bg-white/90'}`}>
            <CardHeader>
              <CardTitle className="flex flex-wrap items-center justify-between gap-3">
                <span className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Operations Lab
                </span>
                <div className="flex items-center gap-2">
                  <Button variant={warRoomMode ? 'default' : 'outline'} size="sm" onClick={() => setWarRoomMode((prev) => !prev)}>
                    {warRoomMode ? 'War Room ON' : 'Enable War Room'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => void exportExecutiveSnapshot()} disabled={!opsPermissions.canExportSnapshot}>
                    <Download className="w-4 h-4 mr-2" />
                    Export Snapshot
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className={`rounded-xl p-4 ${warRoomMode ? 'bg-slate-800/80 border border-slate-700' : 'bg-blue-50 border border-blue-100'}`}>
                  <div className="text-xs uppercase tracking-wide opacity-70 mb-1">Momentum Score</div>
                  <div className="text-3xl font-bold">{momentumScore}</div>
                </div>
                <div className={`rounded-xl p-4 ${warRoomMode ? 'bg-slate-800/80 border border-slate-700' : 'bg-amber-50 border border-amber-100'}`}>
                  <div className="text-xs uppercase tracking-wide opacity-70 mb-1">Anomalies</div>
                  <div className="text-3xl font-bold">{runtimeAnomalies.length}</div>
                </div>
                <div className={`rounded-xl p-4 ${warRoomMode ? 'bg-slate-800/80 border border-slate-700' : 'bg-green-50 border border-green-100'}`}>
                  <div className="text-xs uppercase tracking-wide opacity-70 mb-1">Resilience</div>
                  <div className="text-3xl font-bold">{runtimeSummary.resilienceScore}</div>
                </div>
                <div className={`rounded-xl p-4 ${warRoomMode ? 'bg-slate-800/80 border border-slate-700' : 'bg-cyan-50 border border-cyan-100'}`}>
                  <div className="text-xs uppercase tracking-wide opacity-70 mb-1">Live Incidents</div>
                  <div className="text-3xl font-bold">{runtimeIncidentFeed.length}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className={`rounded-xl p-4 ${warRoomMode ? 'bg-slate-800/70 border border-slate-700' : 'bg-white border border-slate-200'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold">Risk Radar</h4>
                    <Badge variant="outline">Live</Badge>
                  </div>
                  <div className="space-y-2">
                    {riskRadar.map((riskItem) => (
                      <div key={riskItem.domain} className="flex items-center justify-between rounded-lg border px-3 py-2">
                        <div>
                          <div className="text-sm font-medium">{riskItem.domain}</div>
                          <div className="text-xs opacity-70">{riskItem.status}</div>
                        </div>
                        <div className="text-lg font-semibold">{Math.round(riskItem.risk)}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={`rounded-xl p-4 ${warRoomMode ? 'bg-slate-800/70 border border-slate-700' : 'bg-white border border-slate-200'}`}>
                  <h4 className="font-semibold mb-3">Strategic Insights</h4>
                  <div className="space-y-2 max-h-52 overflow-auto pr-1">
                    {strategicInsights.map((insight) => (
                      <div key={insight} className="text-sm rounded-lg border px-3 py-2">
                        {insight}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className={`rounded-xl p-4 ${warRoomMode ? 'bg-slate-800/70 border border-slate-700' : 'bg-white border border-slate-200'}`}>
                  <h4 className="font-semibold mb-3">Simulation Console</h4>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => runIncidentSimulation('fx')}>Inject FX Outage</Button>
                    <Button size="sm" variant="outline" onClick={() => runIncidentSimulation('chat')}>Inject Chat Permission Fault</Button>
                    <Button size="sm" variant="outline" onClick={() => runIncidentSimulation('exchange')}>Inject Exchange Feed Degrade</Button>
                  </div>
                  <p className="text-xs opacity-70 mt-3">
                    Simulations write structured runtime events so your recovery pipeline can be validated without waiting for real incidents.
                  </p>
                </div>

                <div className={`rounded-xl p-4 ${warRoomMode ? 'bg-slate-800/70 border border-slate-700' : 'bg-white border border-slate-200'}`}>
                  <h4 className="font-semibold mb-3">Strategic Insights</h4>
                  <div className="space-y-2 max-h-52 overflow-auto pr-1">
                    {strategicInsights.map((insight) => (
                      <div key={insight} className="text-sm rounded-lg border px-3 py-2">
                        {insight}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className={`rounded-xl p-4 ${warRoomMode ? 'bg-slate-800/70 border border-slate-700' : 'bg-white border border-slate-200'}`}>
                  <h4 className="font-semibold mb-3">Simulation Console</h4>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => void runIncidentSimulation('fx')} disabled={!opsPermissions.canRunSimulations}>Inject FX Outage</Button>
                    <Button size="sm" variant="outline" onClick={() => void runIncidentSimulation('chat')} disabled={!opsPermissions.canRunSimulations}>Inject Chat Permission Fault</Button>
                    <Button size="sm" variant="outline" onClick={() => void runIncidentSimulation('exchange')} disabled={!opsPermissions.canRunSimulations}>Inject Exchange Feed Degrade</Button>
                  </div>
                  <p className="text-xs opacity-70 mt-3">
                    Simulations write structured runtime events so your recovery pipeline can be validated without waiting for real incidents.
                  </p>
                  {!opsPermissions.canRunSimulations && (
                    <p className="text-xs mt-2 text-amber-600">Restricted: incident simulation is available only to Super Admin.</p>
                  )}
                </div>

                <div className={`rounded-xl p-4 ${warRoomMode ? 'bg-slate-800/70 border border-slate-700' : 'bg-white border border-slate-200'}`}>
                  <h4 className="font-semibold mb-3">Incident Feed Search</h4>
                  <div className="relative mb-3">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
                    <input
                      value={opsQuery}
                      onChange={(event) => setOpsQuery(event.target.value)}
                      placeholder="Filter runtime keys, severity, or timestamp"
                      className="w-full h-10 rounded-md border border-input bg-background pl-9 pr-3 text-sm"
                    />
                  </div>
                  <div className="space-y-2 max-h-56 overflow-auto pr-1">
                    {runtimeIncidentFeed
                      .filter((event) => {
                        const q = opsQuery.trim().toLowerCase();
                        if (!q) return true;
                        return `${event.key} ${event.severity} ${event.timestamp}`.toLowerCase().includes(q);
                      })
                      .map((event, index) => (
                        <div key={`${event.key}-${event.timestamp}-${index}`} className="rounded-lg border px-3 py-2 text-xs">
                          <div className="font-medium">{event.key}</div>
                          <div className="opacity-70">{event.severity.toUpperCase()} • {new Date(event.timestamp).toLocaleString()}</div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className={`rounded-xl p-4 ${warRoomMode ? 'bg-slate-800/70 border border-slate-700' : 'bg-white border border-slate-200'}`}>
                  <h4 className="font-semibold mb-3">Role-Scoped Runbook Actions</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between rounded-lg border px-3 py-2">
                      <div>
                        <div className="text-sm font-medium">Export Runtime Diagnostics</div>
                        <div className="text-xs opacity-70">Allowed for Admin and Super Admin</div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => void runOpsRunbookAction('export_diagnostics')} disabled={!opsPermissions.canExportDiagnostics}>Run</Button>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border px-3 py-2">
                      <div>
                        <div className="text-sm font-medium">Apply Recommended Recovery</div>
                        <div className="text-xs opacity-70">Allowed for Super Admin only</div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => void runOpsRunbookAction('recommended_recovery')} disabled={!opsPermissions.canRunRecovery}>Run</Button>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border px-3 py-2">
                      <div>
                        <div className="text-sm font-medium">Clear FX and Exchange Caches</div>
                        <div className="text-xs opacity-70">Allowed for Super Admin only</div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => void runOpsRunbookAction('clear_caches')} disabled={!opsPermissions.canClearCaches}>Run</Button>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border px-3 py-2">
                      <div>
                        <div className="text-sm font-medium">Reset Runtime Metrics</div>
                        <div className="text-xs opacity-70">Allowed for Super Admin only</div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => void runOpsRunbookAction('reset_metrics')} disabled={!opsPermissions.canResetMetrics}>Run</Button>
                    </div>
                  </div>
                </div>

                <div className={`rounded-xl p-4 ${warRoomMode ? 'bg-slate-800/70 border border-slate-700' : 'bg-white border border-slate-200'}`}>
                  <h4 className="font-semibold mb-3">Runbook Execution History (Firestore)</h4>
                  {runbookHistoryLoading ? (
                    <div className="text-sm opacity-70">Loading runbook history...</div>
                  ) : runbookHistory.length === 0 ? (
                    <div className="text-sm opacity-70">No runbook executions logged yet.</div>
                  ) : (
                    <div className="space-y-2 max-h-72 overflow-auto pr-1">
                      {runbookHistory.map((entry) => (
                        <div key={entry.id} className="rounded-lg border px-3 py-2">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <div className="text-sm font-medium">{entry.title}</div>
                              <div className="text-xs opacity-70">{entry.actorEmail} • {entry.actorRole}</div>
                              {entry.details && <div className="text-xs opacity-70 mt-1">{entry.details}</div>}
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <Badge
                                className={
                                  entry.status === 'success'
                                    ? 'bg-green-100 text-green-800 border-green-200'
                                    : entry.status === 'blocked'
                                      ? 'bg-amber-100 text-amber-800 border-amber-200'
                                      : 'bg-red-100 text-red-800 border-red-200'
                                }
                              >
                                {entry.status.toUpperCase()}
                              </Badge>
                              <Badge variant="outline">{entry.scope.toUpperCase()}</Badge>
                            </div>
                          </div>
                          <div className="text-[11px] opacity-70 mt-1">
                            {entry.createdAt > 0 ? new Date(entry.createdAt).toLocaleString() : 'Pending timestamp sync'}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Runtime Resilience Metrics
                </span>
                <div className="flex items-center gap-2">
                  <Badge
                    className={
                      runtimeSummary.status === 'stable'
                        ? 'bg-green-100 text-green-800 border-green-200'
                        : runtimeSummary.status === 'degraded'
                          ? 'bg-amber-100 text-amber-800 border-amber-200'
                          : 'bg-red-100 text-red-800 border-red-200'
                    }
                  >
                    {runtimeSummary.status.toUpperCase()} • {runtimeSummary.resilienceScore}/100
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const diagnostics = getRuntimeDiagnosticsBundle();
                      const blob = new Blob([JSON.stringify(diagnostics, null, 2)], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `runtime-diagnostics-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`;
                      link.click();
                      URL.revokeObjectURL(url);
                      toast({
                        title: 'Diagnostics exported',
                        description: 'Runtime diagnostics bundle has been downloaded.',
                      });
                    }}
                  >
                    Export Diagnostics
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const result = applyRecommendedRuntimeRecovery();
                      setRuntimeMetrics(getRuntimeMetricsSnapshot());
                      setRuntimeHistory(getRuntimeResilienceHistory());
                      toast({
                        title: 'Recommended recovery executed',
                        description: result.actions.join(' • '),
                      });
                    }}
                  >
                    Apply Recommended Recovery
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const result = clearRuntimeRecoveryCaches();
                      setRuntimeMetrics(getRuntimeMetricsSnapshot());
                      setRuntimeHistory(getRuntimeResilienceHistory());
                      toast({
                        title: 'Recovery action completed',
                        description: result.cleared.length > 0
                          ? `Cleared caches: ${result.cleared.join(', ')}`
                          : 'No caches were cleared in this environment.',
                      });
                    }}
                  >
                    Clear FX/Exchange Caches
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      clearRuntimeMetrics();
                      setRuntimeMetrics(getRuntimeMetricsSnapshot());
                      setRuntimeHistory(getRuntimeResilienceHistory());
                      toast({
                        title: 'Runtime metrics reset',
                        description: 'Reliability counters were cleared.',
                      });
                    }}
                  >
                    Reset Metrics
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground mb-1">Resilience Score</div>
                  <div className="text-2xl font-bold">{runtimeSummary.resilienceScore}</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground mb-1">Events (24h)</div>
                  <div className="text-2xl font-bold">{runtimeSummary.totalEvents}</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground mb-1">Degradation Signals</div>
                  <div className="text-2xl font-bold">{runtimeSummary.degradationEvents}</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground mb-1">Recovery Signals</div>
                  <div className="text-2xl font-bold">{runtimeSummary.recoveryEvents}</div>
                </div>
              </div>

              <div className="rounded-md border bg-muted/30 p-3 mb-5">
                <div className="text-xs font-semibold mb-2">Recommended Actions</div>
                <div className="space-y-1">
                  {runtimeSummary.recommendations.map((item) => (
                    <div key={item} className="text-xs text-muted-foreground">• {item}</div>
                  ))}
                </div>
              </div>

              <div className="rounded-md border bg-muted/30 p-3 mb-5">
                <div className="text-xs font-semibold mb-2">Anomaly Alerts</div>
                {runtimeAnomalies.length === 0 ? (
                  <div className="text-xs text-muted-foreground">No anomalies detected in the recent resilience trend window.</div>
                ) : (
                  <div className="space-y-2">
                    {runtimeAnomalies.map((anomaly) => (
                      <div key={anomaly.id} className="flex items-center justify-between rounded border bg-background p-2">
                        <div>
                          <div className="text-xs font-medium">{anomaly.message}</div>
                          <div className="text-xs text-muted-foreground">
                            Current: {anomaly.currentValue} • Baseline: {anomaly.baselineValue} • Delta: {anomaly.delta}
                          </div>
                        </div>
                        <Badge
                          className={
                            anomaly.severity === 'critical'
                              ? 'bg-red-100 text-red-800 border-red-200'
                              : anomaly.severity === 'high'
                                ? 'bg-amber-100 text-amber-800 border-amber-200'
                                : 'bg-blue-100 text-blue-800 border-blue-200'
                          }
                        >
                          {anomaly.severity.toUpperCase()}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-md border bg-muted/30 p-3 mb-5">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="text-xs font-semibold">Auto-Recovery Autopilot</div>
                  <Button
                    variant={autoRecoveryEnabled ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setAutoRecoveryEnabled((prev) => !prev)}
                  >
                    {autoRecoveryEnabled ? 'Enabled' : 'Disabled'}
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground">
                  Auto-recovery runs only on high/critical anomalies and enforces a {Math.round(AUTO_RECOVERY_COOLDOWN_MS / 60000)} minute cooldown.
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Last auto-recovery: {lastAutoRecoveryAt > 0 ? new Date(lastAutoRecoveryAt).toLocaleString() : 'Never'}
                </div>
              </div>

              <div className="rounded-md border bg-muted/30 p-3 mb-5">
                <div className="text-xs font-semibold mb-2">Runtime Incident Feed</div>
                {runtimeIncidentFeed.length === 0 ? (
                  <div className="text-xs text-muted-foreground">No runtime incidents captured yet.</div>
                ) : (
                  <div className="space-y-2 max-h-44 overflow-auto">
                    {runtimeIncidentFeed.map((event, index) => (
                      <div key={`${event.key}-${event.timestamp}-${index}`} className="flex items-center justify-between rounded border bg-background p-2">
                        <div className="text-xs">
                          <div className="font-medium">{event.key}</div>
                          <div className="text-muted-foreground">{new Date(event.timestamp).toLocaleString()}</div>
                        </div>
                        <Badge
                          className={
                            event.severity === 'critical'
                              ? 'bg-red-100 text-red-800 border-red-200'
                              : event.severity === 'warning'
                                ? 'bg-amber-100 text-amber-800 border-amber-200'
                                : 'bg-blue-100 text-blue-800 border-blue-200'
                          }
                        >
                          {event.severity.toUpperCase()}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-md border bg-muted/30 p-3 mb-5">
                <div className="text-xs font-semibold mb-2">Resilience Trend (Recent Samples)</div>
                {trendPoints.length === 0 ? (
                  <div className="text-xs text-muted-foreground">No trend data yet.</div>
                ) : (
                  <>
                    <div className="flex items-end gap-1 h-24 mb-2">
                      {trendPoints.map((point) => (
                        <div
                          key={`${point.timestamp}-${point.resilienceScore}`}
                          className={`flex-1 rounded-sm ${
                            point.status === 'stable'
                              ? 'bg-green-400/80'
                              : point.status === 'degraded'
                                ? 'bg-amber-400/80'
                                : 'bg-red-400/80'
                          }`}
                          style={{ height: `${Math.max(6, Math.min(100, point.resilienceScore))}%` }}
                          title={`${new Date(point.timestamp).toLocaleString()} • ${point.resilienceScore}`}
                        />
                      ))}
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-xs text-muted-foreground">
                      <div>
                        Avg:{' '}
                        {Math.round(
                          trendPoints.reduce((sum, point) => sum + point.resilienceScore, 0) /
                            Math.max(1, trendPoints.length)
                        )}
                      </div>
                      <div>
                        Min:{' '}
                        {Math.min(...trendPoints.map((point) => point.resilienceScore))}
                      </div>
                      <div>
                        Max:{' '}
                        {Math.max(...trendPoints.map((point) => point.resilienceScore))}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground mb-1">FX Provider Failures</div>
                  <div className="text-2xl font-bold">{runtimeMetrics.counters['fx.provider.failure'] || 0}</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground mb-1">FX Fallback Usage</div>
                  <div className="text-2xl font-bold">{runtimeMetrics.counters['fx.fallback.used'] || 0}</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground mb-1">Exchange Demo Mode</div>
                  <div className="text-2xl font-bold">{runtimeMetrics.counters['exchange.price_feed.demo_mode'] || 0}</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground mb-1">Typing Permission Denied</div>
                  <div className="text-2xl font-bold">{runtimeMetrics.counters['chat.typing.permission_denied'] || 0}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground mb-1">Staking Fetch Errors</div>
                  <div className="text-xl font-semibold">{runtimeMetrics.counters['staking.fetch.error'] || 0}</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground mb-1">Staking Fallback Recovered</div>
                  <div className="text-xl font-semibold">{runtimeMetrics.counters['staking.read.fallback_recovered'] || 0}</div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground mb-1">Blocked High-Risk Swaps</div>
                  <div className="text-xl font-semibold">{runtimeMetrics.counters['exchange.swap.blocked_high_risk'] || 0}</div>
                </div>
              </div>

              <div className="text-xs text-muted-foreground mb-2">
                Last metric update: {runtimeMetrics.updatedAt ? new Date(runtimeMetrics.updatedAt).toLocaleString() : 'No events yet'}
              </div>
              <div className="rounded-md border bg-muted/30 p-3 max-h-52 overflow-auto space-y-2">
                {runtimeMetrics.recentEvents.length === 0 ? (
                  <div className="text-xs text-muted-foreground">No runtime events captured yet.</div>
                ) : (
                  runtimeMetrics.recentEvents.slice(0, 15).map((event, index) => (
                    <div key={`${event.key}-${event.timestamp}-${index}`} className="text-xs">
                      <span className="font-medium">{event.key}</span>
                      <span className="text-muted-foreground"> • {new Date(event.timestamp).toLocaleTimeString()}</span>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Notification Deep-Link Migration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Backfills older notifications so clicking always opens the correct post/message/call.
              </div>

              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm">Page size</span>
                  <input
                    className="h-9 w-24 rounded-md border border-input bg-background px-3 text-sm"
                    type="number"
                    min={1}
                    max={500}
                    value={notifMigrationPageSize}
                    onChange={(e) => setNotifMigrationPageSize(Math.max(1, Math.min(500, Number(e.target.value) || 200)))}
                  />
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={notifMigrationIncludeMissing}
                    onChange={(e) => setNotifMigrationIncludeMissing(e.target.checked)}
                  />
                  Also set missing actionUrl
                </label>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  disabled={notifMigrationLoading}
                  onClick={() => runNotificationMigration(true)}
                >
                  {notifMigrationLoading ? 'Working…' : 'Dry run (this page)'}
                </Button>

                <Button
                  disabled={notifMigrationLoading}
                  onClick={() => runNotificationMigration(false)}
                >
                  {notifMigrationLoading ? 'Working…' : 'Run migration (this page)'}
                </Button>

                <Button
                  variant="secondary"
                  disabled={notifMigrationLoading || !notifMigrationNextToken}
                  onClick={() => runNotificationMigration(false)}
                >
                  Run next page
                </Button>

                <Button
                  variant="ghost"
                  disabled={notifMigrationLoading}
                  onClick={() => {
                    setNotifMigrationNextToken(null);
                    setNotifMigrationLastResult(null);
                  }}
                >
                  Reset
                </Button>

                {notifMigrationNextToken ? (
                  <Badge variant="outline">Next page token: {notifMigrationNextToken}</Badge>
                ) : (
                  <Badge variant="outline">No next page</Badge>
                )}
              </div>

              {notifMigrationLastResult && (
                <div className="rounded-md border bg-muted/30 p-3 text-xs overflow-auto max-h-64">
                  <pre>{JSON.stringify(notifMigrationLastResult, null, 2)}</pre>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Security Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold mb-3">Security Status</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-success/10 rounded-lg">
                      <div>
                        <div className="font-medium text-success">Firebase Security</div>
                        <div className="text-sm text-success/80">Rules Active</div>
                      </div>
                      <Shield className="w-5 h-5 text-success" />
                    </div>
                    <div className="flex justify-between items-center p-3 bg-success/10 rounded-lg">
                      <div>
                        <div className="font-medium text-success">Authentication</div>
                        <div className="text-sm text-success/80">Secured</div>
                      </div>
                      <Lock className="w-5 h-5 text-success" />
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-3">User Security</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Admin Users:</span>
                      <span className="font-medium">{userStats.adminUsers}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Super Admins:</span>
                      <span className="font-medium">{userStats.roleDistribution.superadmin || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Verified Users:</span>
                      <span className="font-medium">{userStats.verifiedUsers}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
          <p>Last updated: {new Date(stats.lastUpdated).toLocaleString()}</p>
          <p>InTouch Super Admin Dashboard - Real Data • Milan Krnjajić</p>
        </div>
      </div>
    </div>
  );
}